// Auth provider for the production data layer. Talks to the live auth-service
// via authClient. Exposes the session + login/verifyMfa/logout/refresh through
// the useAuth() hook.

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { authClient, isLoggedOut, type LoginResponse, type Role } from "@/lib/auth/authClient";
import { rolesFromClaims, type JwtClaims } from "@/lib/auth/jwt";
import { tokenStore } from "@/lib/auth/tokenStore";

// Persisted "this browser has logged in before" marker. The access token lives
// only in memory, so on a hard reload we have no way to know whether a refresh
// cookie might exist. This flag gates the silent mount refresh: first-time
// visitors (no marker) never hit /auth/refresh, so /login produces no 401 noise.
const WAS_LOGGED_IN_KEY = "pb_admin_was_logged_in";

function markLoggedIn(): void {
  try {
    localStorage.setItem(WAS_LOGGED_IN_KEY, "1");
  } catch {
    /* storage unavailable (private mode) — refresh just won't auto-fire */
  }
}

function clearLoggedInMarker(): void {
  try {
    localStorage.removeItem(WAS_LOGGED_IN_KEY);
  } catch {
    /* ignore */
  }
}

function hasLoggedInMarker(): boolean {
  try {
    return localStorage.getItem(WAS_LOGGED_IN_KEY) === "1";
  } catch {
    return false;
  }
}

type LoginOutcome =
  | { kind: "success" }
  | { kind: "mfa_required"; challengeToken: string; mfaSetupRequired: boolean };

interface AuthState {
  user:
    | {
        userId: number;
        tenantId: number;
        tenantSlug: string;
        email: string;
        role: Role;
        mfaSetupRequired: boolean;
      }
    | null;
  roles: string[];
  loading: boolean;
  login: (email: string, password: string) => Promise<LoginOutcome>;
  verifyMfa: (challengeToken: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  // Clears the mfaSetupRequired flag on the in-memory session once a logged-in
  // user finishes the forced enrollment flow (RequireMfaEnrolled).
  markMfaEnrolled: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

function userFromClaims(claims: JwtClaims | null): AuthState["user"] {
  if (!claims?.sub || claims.tenant_id == null || !claims.email || !claims.role) {
    return null;
  }
  return {
    userId: Number(claims.sub),
    tenantId: claims.tenant_id,
    tenantSlug: claims.tenant_slug ?? "",
    email: claims.email,
    role: claims.role as Role,
    mfaSetupRequired: false,
  };
}

function applyTokens(response: LoginResponse): LoginOutcome {
  if ("mfa_required" in response) {
    return {
      kind: "mfa_required",
      challengeToken: response.challenge_token,
      // First-login enrollment vs. plain verify for an already-enrolled user.
      mfaSetupRequired: response.mfa_setup_required,
    };
  }
  tokenStore.set(response.tokens.access_token);
  return { kind: "success" };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthState["user"]>(() => userFromClaims(tokenStore.jwtClaims));
  const [loading, setLoading] = useState<boolean>(() => !tokenStore.accessToken);
  const [roles, setRoles] = useState<string[]>(() => rolesFromClaims(tokenStore.jwtClaims));

  // Keep `roles` in sync with the access token's claims.
  useEffect(() => {
    function syncToken(token: string | null) {
      if (!token) {
        setRoles([]);
        setUser(null);
        return;
      }
      const claims = tokenStore.jwtClaims;
      setRoles(rolesFromClaims(claims));
      setUser(userFromClaims(claims));
    }
    const unsubscribe = tokenStore.subscribe(syncToken);
    syncToken(tokenStore.accessToken);
    return unsubscribe;
  }, []);

  // Silent refresh on mount — covers full reloads where the HTTP-only cookie
  // is still valid but the in-memory access token was lost. Gated on the
  // "was-logged-in" marker: first-time visitors never call /auth/refresh, so a
  // fresh /login load makes no 401 round trip.
  useEffect(() => {
    if (tokenStore.accessToken) {
      setLoading(false);
      return;
    }
    if (!hasLoggedInMarker()) {
      // Never logged in from this browser — skip the refresh entirely.
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const result = await authClient.refresh();
        if (cancelled) return;
        if (isLoggedOut(result)) {
          // Expected logged-out state (cookie expired/cleared). Drop the stale
          // marker so we don't keep retrying on every reload. No error surfaced.
          clearLoggedInMarker();
          return;
        }
        tokenStore.set(result.tokens.access_token);
        // Rebuild the session user from the (server-signed) access-token
        // claims so a full page reload keeps the session. Claims are only
        // trusted as display hints — every API call is re-authorized
        // server-side. No fabricated fallbacks: missing identity claims mean
        // the session is treated as not logged in.
        const claims = tokenStore.jwtClaims;
        if (claims?.sub && claims.tenant_id != null && claims.email && claims.role) {
          setUser({
            userId: Number(claims.sub),
            tenantId: claims.tenant_id,
            tenantSlug: claims.tenant_slug ?? "",
            email: claims.email,
            role: claims.role as Role,
            mfaSetupRequired: false,
          });
        }
      } catch {
        // A real refresh failure — treat as logged out; UI redirects to /login.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // External signal from the urql auth exchange when refresh fails mid-flight.
  useEffect(() => {
    function onExpired(): void {
      clearLoggedInMarker();
      tokenStore.clear();
      setUser(null);
    }
    window.addEventListener("powerbess:auth-expired", onExpired);
    return () => window.removeEventListener("powerbess:auth-expired", onExpired);
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<LoginOutcome> => {
    const response = await authClient.login(email, password);
    const outcome = applyTokens(response);
    if (outcome.kind === "success" && !("mfa_required" in response)) {
      // Tokens issued — remember this browser logged in so a later reload can
      // silently refresh.
      markLoggedIn();
      setUser({
        userId: response.user_id,
        tenantId: response.tenant_id,
        tenantSlug: response.tenant_slug,
        email: response.email,
        role: response.role,
        mfaSetupRequired: response.mfa_setup_required,
      });
    }
    return outcome;
  }, []);

  const verifyMfa = useCallback(async (challengeToken: string, code: string) => {
    const response = await authClient.verifyMfa(challengeToken, code);
    tokenStore.set(response.tokens.access_token);
    markLoggedIn();
    setUser({
      userId: response.user_id,
      tenantId: response.tenant_id,
      tenantSlug: response.tenant_slug,
      email: response.email,
      role: response.role,
      mfaSetupRequired: response.mfa_setup_required,
    });
  }, []);

  const logout = useCallback(async () => {
    try {
      await authClient.logout();
    } finally {
      clearLoggedInMarker();
      tokenStore.clear();
      setUser(null);
    }
  }, []);

  const refresh = useCallback(async () => {
    const result = await authClient.refresh();
    if (isLoggedOut(result)) {
      clearLoggedInMarker();
      tokenStore.clear();
      setUser(null);
      return;
    }
    tokenStore.set(result.tokens.access_token);
  }, []);

  const markMfaEnrolled = useCallback(() => {
    setUser((prev) => (prev ? { ...prev, mfaSetupRequired: false } : prev));
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, roles, loading, login, verifyMfa, logout, refresh, markMfaEnrolled }),
    [user, roles, loading, login, verifyMfa, logout, refresh, markMfaEnrolled],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}
